"use strict";

var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _dayjs = _interopRequireDefault(require("../utils/dayjs"));
var _isEmpty = _interopRequireDefault(require("lodash/isEmpty"));
var _isNumber = _interopRequireDefault(require("lodash/isNumber"));
var _isString = _interopRequireDefault(require("lodash/isString"));
var _number = _interopRequireDefault(require("../number/number"));
var _string = _interopRequireDefault(require("../string/string"));
/**
 * Utilities for working with times.
 * @module TimeUtils
 */
const TimeUtils = {
  /**
   * Normalizes the provided time down to the provided step.
   * @static
   * @function normalizeToStep
   * @param {String} time - The input value in the format hh:mm A.
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.normalizeToStep('07:07 PM', 15); // '07:00 PM'
   * @returns {String} - The normalized time.
   */
  normalizeToStep(time, step = 1) {
    if ((0, _isEmpty.default)(time)) {
      return time;
    }
    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }
    const {
      hours,
      mins
    } = TimeUtils.getValuesFromString(time, step);
    return TimeUtils.getFromValuesAsString(hours, mins);
  },
  /**
   * Provides the hours, minutes, and meridiem from a time string.
   * @static
   * @function getValuesFromString
   * @param {String} time - The input value in the format hh:mm A.
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.getValuesFromString('10:19 AM');
   * @returns {Object} - The object containing the hours, minutes, and meridiem.
   */
  getValuesFromString(time, step = 1) {
    if ((0, _isEmpty.default)(time)) {
      return time;
    }
    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }
    const firstPartials = time.split(':');
    const secondPartials = firstPartials[1].split(' ');
    const isAM = secondPartials[1] === 'AM';
    const mins = parseInt(secondPartials[0], 10);
    const minsRounded = _number.default.roundDownToNearestStep(mins, step);
    let hours = parseInt(firstPartials[0], 10);
    if (isAM && hours === 12) {
      hours = 0;
    } else if (!isAM && hours !== 12) {
      hours += 12;
    }
    return {
      hours,
      mins: minsRounded,
      isAM
    };
  },
  /**
   * Provides the formatted time from hours and minutes values.
   * @static
   * @function getFromValuesAsString
   * @param {Number} hours - The hours in a 24 hour format.
   * @param {Number} minutes - The minutes from 0 - 60.
   * @param {Boolean} [roundMinutesDown=false] - Whether to round the minutes down to the provided step.
   * @param {Number} [step=1] - The step to round minutes down to.
   * @example
   * TimeUtils.getFromValuesAsString(20, 35); // '08:35 PM'
   * @returns {String} - The formatted time.
   */
  getFromValuesAsString(hours, minutes, roundMinutesDown = false, step = 1) {
    const meridiem = hours < 12 ? 'AM' : 'PM';
    let newHours = hours;
    let newMinutes = minutes;
    if (newHours > 12) {
      newHours -= 12;
    } else if (newHours === 0) {
      newHours = 12;
    }
    if (roundMinutesDown) {
      newMinutes = _number.default.roundDownToNearestStep(newMinutes, step);
    }
    return `${_string.default.padWith(newHours)}:${_string.default.padWith(newMinutes)} ${meridiem}`;
  },
  /**
   * Converts a mobile input time to a string in the format hh:mm A.
   * @static
   * @function getMobileAsString
   * @param {String} time - The input value in the format HH:mm (24 hour format).
   * @param {Number} [step=1] - The step to round down to.
   * @example
   * TimeUtils.getMobileAsString('22:19'); // 10:19 PM
   * @returns {String} - The formatted time.
   */
  getMobileAsString(time, step = 1) {
    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }
    const frags = time.split(':');
    const mins = parseInt(frags[1], 10);
    const minsRounded = _number.default.roundDownToNearestStep(mins, step);
    const hours = parseInt(frags[0], 10);
    const meridiem = hours < 12 ? 'AM' : 'PM';
    let hh = hours;
    if (hours > 12) {
      hh -= 12;
    } else if (hours === 0) {
      hh = 12;
    }
    return `${_string.default.padWith(hh)}:${_string.default.padWith(minsRounded)} ${meridiem}`;
  },
  /**
   * Converts a time to a date object.
   * @static
   * @function getAsDate
   * @param {String} time - The input value in the format hh:mm A.
   * @example
   * TimeUtils.getAsDate('10:19 PM');
   * @returns {Date} - The time inside of a date object.
   */
  getAsDate(time) {
    if ((0, _isEmpty.default)(time)) {
      return null;
    }
    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }
    const {
      hours,
      mins
    } = TimeUtils.getValuesFromString(time);
    const d = new Date();
    d.setHours(hours);
    d.setMinutes(mins);
    d.setSeconds(0);
    return d;
  },
  /**
   * Converts a time to a dayjs instance.
   * @static
   * @function getAsDayjs
   * @param {String} time - The input value in the format hh:mm A.
   * @example
   * TimeUtils.getAsDayjs('10:19 PM');
   * @returns {Dayjs} - The time inside of a day instance.
   */
  getAsDayjs(time) {
    if ((0, _isEmpty.default)(time)) {
      return null;
    }
    if (!(0, _isString.default)(time)) {
      throw new Error('The supplied time is not a string.');
    }
    const {
      hours,
      mins
    } = TimeUtils.getValuesFromString(time);
    return (0, _dayjs.default)().startOf('day').set('hour', hours).set('minute', mins);
  },
  /**
   * Generate an array of times.
   * @static
   * @function getTimes
   * @param {Object} data - The data to pass to the function.
   * @param {Number} [data.step=30] - Increment times by this many minutes.
   * @param {String} [data.format='hh:mm A'] - the format for the date.
   * @param {String} [data.ignoreBefore] - Number of minutes to ignore times before in the output.
   * @param {String} [data.ignoreAfter] - Number of minutes to ignore times after in the output.
   * @example
   * TimeUtils.getTimes({
   *     step: 30,
   *     format: 'hh:mm A'
   * });
   * @returns {Array} times - An array of times.
   */
  getTimes({
    step = 30,
    format = 'hh:mm A',
    ignoreBefore,
    ignoreAfter
  } = {}) {
    if (ignoreBefore && !(0, _isNumber.default)(ignoreBefore)) {
      throw new Error('The ignoreBefore value passed was not a number.');
    }
    if (ignoreAfter && !(0, _isNumber.default)(ignoreAfter)) {
      throw new Error('The ignoreAfter value passed was not a number.');
    }
    if (ignoreBefore && ignoreAfter && ignoreBefore > ignoreAfter) {
      throw new Error('The value of ignoreBefore should be less than the value of ignoreAfter.');
    }
    const dateTime = (0, _dayjs.default)();
    const total = 1440;
    const len = total / step;
    const times = [];
    const before = ignoreBefore ? dateTime.startOf('day').set('minute', ignoreBefore) : null;
    const after = ignoreAfter ? dateTime.startOf('day').set('minute', ignoreAfter) : null;
    let i = 0;
    let id = 0;
    let shouldAdd = true;
    let time;
    for (i; i < len; i++) {
      id = i * step;
      time = dateTime.startOf('day').set('minute', id);
      shouldAdd = before ? time.isSameOrAfter(before) : shouldAdd;
      if (shouldAdd) {
        shouldAdd = after ? time.isSameOrBefore(after) : shouldAdd;
      }
      if (shouldAdd) {
        times.push({
          id,
          formatted: time.format(format)
        });
      }
    }
    return times;
  }
};
var _default = exports.default = TimeUtils;